<template>
  <div class="modal-card admin-players-form">
    <b-loading :is-full-page="false" v-model="isLoading"></b-loading>

    <header class="modal-card-head">
      <div class="is-size-5 has-text-info mx-2">
        {{ players.length }}
        {{ tournament.playerLimit ? "/" + tournament.playerLimit : "" }}
        PLAYERS
      </div>

      <b-field label="order by" label-position="on-border" type="is-info">
        <b-select v-model="playersOrderBy">
          <option value="startingRank" key="orderStartingRank">
            starting rank
          </option>
          <option value="name" key="orderName">name</option>
          <option value="score" key="orderScore">score</option>
          <option value="seed" key="orderSeed">rating</option>
        </b-select>
      </b-field>

      <b-field label="participation" label-position="on-border" type="is-info">
        <b-select v-model="filterStatus">
          <option :value="null" key="all">all</option>
          <option :value="true" key="yes">participating</option>
          <option :value="false" key="no">absent</option>
        </b-select>
      </b-field>
    </header>

    <section class="modal-card-body">
      <!-- Fixed top section -->
      <div class="fixed-top-section">
        <PlayersProgressBar :playersStats="playersStats" />

        <b-field class="pt-3">
          <b-input
            v-model="filterText"
            placeholder="Type to search for players ..."
            type="search"
            icon="search"
          ></b-input>
        </b-field>
      </div>

      <!-- Scrollable players section -->
      <div class="scrollable-players-section">
        <PlayerTags
          :players="sortedPlayers"
          :totalRounds="tournament.totalRounds"
          :canClose="true"
          @onRemovePlayer="removePlayer"
          v-on="$listeners"
        />
      </div>
    </section>

    <!-- Add players -->
    <footer class="modal-card-foot p-3">
      <div class="is-flex is-justify-content-space-between is-flex-grow-1">
        <b-field class="is-flex-grow-1 mb-0">
          <b-input
            type="text"
            placeholder="Enter player usernames here; separated by whitespace, comma, tab or new line"
            expanded
            v-model="playerUsernames"
          ></b-input>
          <b-button
            type="is-primary"
            label="Add Players"
            :disabled="!playerUsernames"
            @click="queryPlayers(playerUsernames)"
          />
        </b-field>

        <b-button
          type="is-success"
          label="Refresh Profiles"
          :disabled="!players.length"
          @click="queryPlayers()"
        />
        <b-button
          v-if="tournament.privacy === 'Paid'"
          label="Update Licenses"
          type="is-warning"
          icon-left="id-card"
          :loading="isCheckingLicenses"
          @click="checkLicenses"
        />

        <!-- Add bots -->
        <b-select
          placeholder="Add a bot player"
          v-model="currentBotPlayer"
          @input="(e) => addBotPlayer(e)"
        >
          <option
            v-for="bot in availableBotPlayers"
            :value="bot.username"
            :key="bot.username"
          >
            {{ bot.first_name }}
          </option>
        </b-select>
      </div>
    </footer>
  </div>
</template>

<script>
import PlayerTags from "@/components/admins/PlayerTags.vue";
import PlayersProgressBar from "@/components/tournaments/PlayersProgressBar.vue";
import { PlayersAPI, MatchesAPI, TournamentsAPI } from "@/services/api";
import { aiPlayer, BOT_PLAYERS } from "vue-chess-shared/utils/ai-helper";
import { firestore } from "@/services/firebase";
import {
  isPlayerInRound,
  toggleRoundsParticipated,
  addPlayer,
  removePlayer,
} from "vue-chess-shared/tournament/helper";

export default {
  components: { PlayerTags, PlayersProgressBar },
  name: "AdminPlayersForm",

  props: ["tournament", "players", "playersStats"],

  data() {
    return {
      isLoading: false,
      playerUsernames: null,
      playersOrderBy: "startingRank",
      filterText: "",
      filterStatus: null,
      subscriptions: [],
      currentBotPlayer: null,
      isCheckingLicenses: false,
    };
  },

  computed: {
    availableBotPlayers() {
      return BOT_PLAYERS.filter(
        (bot) =>
          !this.players ||
          !this.players.find((p) => p.username === bot.username)
      );
    },

    sortedPlayers() {
      const roundNumber = Math.min(
        this.tournament.currentRound + 1,
        this.tournament.totalRounds
      );

      let sorted = this.players.map((p) => ({
        ...p,
        isParticipating: isPlayerInRound(p, roundNumber),
        hasNoLicense:
          this.tournament.privacy === "Paid" &&
          !this.subscriptions.find((s) => s.username === p.username),
      }));

      if (this.filterText) {
        sorted = sorted.filter((p) =>
          p.username.includes(this.filterText.toLowerCase())
        );
      }

      if (this.filterStatus !== null) {
        sorted = sorted.filter((p) => p.isParticipating === this.filterStatus);
      }

      if (this.playersOrderBy === "startingRank") {
        sorted = sorted.sort(
          (a, b) => (a.startingRank || Infinity) - (b.startingRank || Infinity)
        );
      } else if (this.playersOrderBy === "score") {
        sorted = sorted.sort((a, b) => b.score - a.score || b.seed - a.seed);
      } else if (this.playersOrderBy === "name") {
        sorted = sorted.sort((a, b) =>
          a.username > b.username ? 1 : a.username < b.username ? -1 : 0
        );
      } else if (this.playersOrderBy === "seed") {
        sorted = sorted.sort((a, b) => b.seed - a.seed);
      }

      return [...sorted];
    },
  },

  methods: {
    async queryPlayers(playerUsernames) {
      this.isLoading = true;
      let usernames = playerUsernames
        ? playerUsernames.match(/[\w]+/g)
        : this.players.map((p) => p.username);
      console.log("queryPlayers", usernames);
      const profiles = await PlayersAPI.get({ playerId: usernames.join(",") });
      if (!profiles || !profiles.length) {
        this.$buefy.toast.open({
          message: "Invalid usernames!",
          type: "is-danger",
        });
        this.isLoading = false;
        return;
      }
      usernames = profiles.map((p) => p.username);
      const ratings = await MatchesAPI.getPlayersStats(usernames);
      if (ratings) {
        ratings.forEach((r) => {
          const profile = profiles.find((p) => p.username === r.player_id);
          if (profile) profile.stats = { rating: r.rating };
        });
      }
      this.addPlayers(profiles);
      this.playerUsernames = "";
      this.isLoading = false;
    },

    async addPlayers(profiles) {
      for (let i = 0; i < profiles.length; i++) {
        const profile = profiles[i];
        await this.addPlayer(profile);
      }

      this.$buefy.toast.open({
        message: `${profiles.length} player(s) added/updated`,
        type: "is-success",
      });
    },

    async addBotPlayer(e) {
      const botPlayer = aiPlayer(e);
      if (!botPlayer) return;
      await addPlayer(firestore, botPlayer, this.tournament);
      toggleRoundsParticipated(firestore, botPlayer, this.tournament);
    },

    async addPlayer(player) {
      await addPlayer(firestore, player, this.tournament);
    },

    removePlayer(player) {
      removePlayer(firestore, player, this.tournament);
      if (player.user.aiLevel) {
        this.currentBotPlayer = undefined;
      }
    },

    async checkLicenses() {
      this.isCheckingLicenses = true;
      // query db for all the licenses of players in tournament
      const subscriptions = await TournamentsAPI.getTournamentSubscriptions(
        this.tournament.id,
        this.tournament.planIds
      );
      this.isCheckingLicenses = false;

      // add players to tournament
      const usernames = subscriptions.map((s) => s.username);
      if (usernames && usernames.length > 0) {
        this.subscriptions = subscriptions;
        if (this.tournament.currentRound < this.tournament.totalRounds) {
          await this.queryPlayers(usernames.join());
        }
      }
    },
  },

  created() {
    if (this.tournament?.currentRound < this.tournament?.totalRounds) {
      this.checkLicenses();
    }
  },
};
</script>

<style scoped>
.admin-players-form {
  display: flex;
  flex-direction: column;
  width: clamp(640px, 90vw, 1080px);
  max-height: 90vh;
}

.modal-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
}

.modal-card-body {
  display: flex;
  flex-direction: column;
  padding: 0;
}

.fixed-top-section {
  padding: 0.75rem;
  border-bottom: 1px solid #dbdbdb;
}

.scrollable-players-section {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
}

.field {
  margin-bottom: 0rem;
}
</style>
